/**
 * Invoicing Store
 *
 * Mirrors the ERPs store: wires the invoicing state + handlers + invariants into
 * a CDSE store and exposes domain-named hooks. The connector catalog is NOT
 * owned here — it lives in the shared systems-integrations store. The
 * `selectInvoicingConnectors` selector below is a thin wrapper over the shared
 * `selectConnectorsByKind` selector, filtered to `kind: 'invoicing'`, so the
 * invoicing subdomain never reimplements the catalog.
 */

import { useCallback } from 'react';
import { createStore } from '@commandDrivenStateEngine/core/createStore';
import type { CommandMetaInput, Dispatch } from '@commandDrivenStateEngine/core/types';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import useAuth from '@contexts/useAuth/useAuth';
import { selectors as sharedSelectors } from '../../shared/state/store';
import type { ConnectorEntry, SystemsIntegrationsState } from '../../shared/state/state';
import { initialState, selectors } from './state';
import { handlers } from './handlers';
import { invariants } from './invariants';
import { InvoicingCommand } from './commands';
import {
  createInvoicingListLoadPolicy,
  createInvoicingSubmitPolicy,
  createInvoicingDeletePolicy
} from './policies';

const store = createStore({
  name: 'invoicing',
  initialState,
  handlers,
  invariants
});

export const {
  useSelector: useInvoicingSelector,
  useErrors: useInvoicingErrors,
  usePolicies: useInvoicingPolicies,
  getState: getInvoicingState,
  dispatch: dispatchInvoicingCommand
} = store;

/**
 * Dispatch hook that attaches the authenticated user to user-sourced commands
 * so the `canApply` capability gates can authorize them. The engine core stays
 * auth-agnostic; this web-app boundary supplies the actor. An explicit
 * `meta.user` (e.g. from tests) is left untouched.
 */
export const useInvoicingDispatch = (): Dispatch<InvoicingCommand> => {
  const auth = useAuth();
  const user = auth?.user ?? null;
  return useCallback<Dispatch<InvoicingCommand>>(
    ( command, metaInput: CommandMetaInput ) =>
      store.dispatch(
        command,
        metaInput.source === 'user' && metaInput.user === undefined
          ? { ...metaInput, user }
          : metaInput
      ),
    [user]
  );
};

/**
 * Policy factory - creates policies that require backend + shared-state access.
 *
 * Mirrors `createChannelsPolicies`: the submit policy needs to read the
 * connector catalog (capabilities) from the SHARED systems-integrations store
 * to branch on `resolveSetupPanelKind`, so the caller supplies a `getConnectors`
 * accessor.
 *
 * @param backend - Client library instance for API calls
 * @param getConnectors - Reads the current connector catalog from shared state
 */
export const createInvoicingPolicies = (
  backend: ClientLibraryType,
  getConnectors: () => Record<string, ConnectorEntry>
) => [
  createInvoicingListLoadPolicy( backend ),
  createInvoicingSubmitPolicy( backend, getConnectors ),
  createInvoicingDeletePolicy( backend )
];

/**
 * Catalog connectors of `kind: 'invoicing'`. Reads the SHARED
 * systems-integrations catalog via `selectConnectorsByKind` rather than
 * reimplementing the filter, mirroring how the erps subdomain pulls its
 * connectors from the shared store.
 */
export const selectInvoicingConnectors = ( state: SystemsIntegrationsState ) =>
  sharedSelectors.selectConnectorsByKind( state, 'invoicing' );

export const {
  selectInvoicingIntegrations,
  selectAddModalOpen
} = selectors;

export { selectors };
